import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import HtmlEditor from './HtmlEditor';

const API_URL = process.env.REACT_APP_API_URL || 'https://app.solteros.it/speeddate/api';

export default function BlogManagement() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', slug: '', excerpt: '', content: '', coverImage: '', category: '', status: 'draft' });
  const [saving, setSaving] = useState(false);

    const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const fetchPosts = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(`${API_URL}/blog`, { headers: { 'x-admin-token': token } });
      setPosts(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error fetching blog posts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPosts(); }, []);

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const formData = new FormData();
      formData.append('image', file);
      const res = await axios.post(`${API_URL}/cover-images`, formData, {
        headers: { 'x-admin-token': token, 'Content-Type': 'multipart/form-data' }
      });
      // Save the data URI (base64) directly, like event covers — persists in the DB
      const data = res.data.data || res.data.url;
      setForm(prev => ({ ...prev, coverImage: data.startsWith('data:') ? data : (data.startsWith('http') ? data : `${window.location.origin}${data}`) }));
      window.Swal.fire({ title: 'Immagine caricata!', icon: 'success', timer: 1500, showConfirmButton: false, toast: true, position: 'top-end' });
    } catch (err) {
      window.Swal.fire('Errore', 'Errore nel caricamento dell\'immagine', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (post) => {
    setEditing(post);
    setForm({
      title: post.title || '',
      slug: post.slug || '',
      excerpt: post.excerpt || '',
      content: post.content || '',
      coverImage: post.coverImage || '',
      category: post.category || '',
      status: post.status || 'draft'
    });
  };

  const handleNew = () => {
    setEditing({});
    setForm({ title: '', slug: '', excerpt: '', content: '', coverImage: '', category: '', status: 'draft' });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.slug || !form.title) {
      window.Swal.fire('Errore', 'Slug e Titolo sono obbligatori', 'error');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(`${API_URL}/blog`, form, { headers: { 'x-admin-token': token } });
      window.Swal.fire({ title: 'Salvato!', icon: 'success', timer: 1500, showConfirmButton: false, toast: true, position: 'top-end' });
      setEditing(null);
      fetchPosts();
    } catch (err) {
      window.Swal.fire('Errore', err.response?.data?.error || 'Errore salvataggio', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (slug) => {
    const result = await window.Swal.fire({
      title: 'Eliminare articolo?',
      text: 'Questa operazione non può essere annullata.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Elimina',
      cancelButtonText: 'Annulla',
      confirmButtonColor: '#d33'
    });
    if (!result.isConfirmed) return;
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API_URL}/blog/${slug}`, { headers: { 'x-admin-token': token } });
      window.Swal.fire({ title: 'Eliminato!', icon: 'success', timer: 1500, showConfirmButton: false, toast: true, position: 'top-end' });
      fetchPosts();
    } catch (err) {
      window.Swal.fire('Errore', err.response?.data?.error || 'Errore cancellazione', 'error');
    }
  };

  if (loading) {
    return (
      <div className="card border-0 shadow-sm">
        <div className="card-body text-center py-5">
          <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Caricamento...</span></div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Fullscreen editor overlay */}
      {editing && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1060, backgroundColor: '#fff', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center px-4 py-3 border-bottom bg-light">
            <div className="d-flex align-items-center gap-3">
              <button className="btn btn-sm btn-outline-secondary rounded-pill" onClick={() => setEditing(null)}>
                <i className="fas fa-arrow-left me-1"></i> Torna alla lista
              </button>
              <h5 className="mb-0 fw-bold">
                <i className="fas fa-edit text-primary me-2"></i>
                {editing?.id ? 'Modifica Articolo' : 'Nuovo Articolo'}
              </h5>
            </div>
            <div className="d-flex gap-2">
              <select className="form-select form-select-sm" style={{ width: 130 }} value={form.status} onChange={e => setForm({...form, status: e.target.value})}>
                <option value="draft">Bozza</option>
                <option value="published">Pubblicato</option>
              </select>
              <button className="btn btn-sm btn-primary rounded-pill px-4 shadow-sm" onClick={handleSave} disabled={saving}>
                <i className="fas fa-save me-1"></i> {saving ? 'Salvataggio...' : 'Salva'}
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="row g-0 flex-grow-1 overflow-hidden">
            <div className="col-lg-8 d-flex flex-column overflow-hidden border-end">
              <div className="p-4 overflow-auto flex-grow-1">
                <div className="mb-3">
                  <input type="text" className="form-control form-control-lg border-0 fw-bold px-0" style={{ fontSize: '1.5rem' }} required
                    placeholder="Titolo dell'articolo..."
                    value={form.title} onChange={e => setForm({...form, title: e.target.value})} />
                </div>
                <div className="mb-3">
                  <input type="text" className="form-control border-0 px-0 text-muted"
                    placeholder="slug-articolo"
                    value={form.slug} onChange={e => setForm({...form, slug: e.target.value})} />
                </div>
                <div className="mb-3">
                  <textarea className="form-control border-0 px-0" rows={2} placeholder="Estratto / Descrizione breve..."
                    value={form.excerpt} onChange={e => setForm({...form, excerpt: e.target.value})} />
                </div>
                <div className="mb-3">
                  <HtmlEditor
                    value={form.content}
                    onChange={html => setForm({...form, content: html})}
                  />
                </div>
              </div>
            </div>
            <div className="col-lg-4 p-4 bg-light overflow-auto">
              <h6 className="fw-bold small text-uppercase text-muted mb-3"><i className="fas fa-cog me-1"></i> Impostazioni</h6>
              <div className="mb-3">
                <label className="form-label small fw-bold text-muted">Categoria</label>
                <input type="text" className="form-control form-control-sm"
                  value={form.category} onChange={e => setForm({...form, category: e.target.value})} />
              </div>
              <div className="mb-3">
                <label className="form-label small fw-bold text-muted">Copertina</label>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  className="d-none"
                  onChange={handleCoverUpload}
                />
                <div className="d-flex gap-2">
                  <button type="button" className="btn btn-sm btn-outline-primary rounded-pill" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                    <i className="fas fa-upload me-1"></i> {uploading ? 'Caricamento...' : 'Carica immagine'}
                  </button>
                  {form.coverImage && (
                    <button type="button" className="btn btn-sm btn-outline-danger rounded-pill" onClick={() => setForm({...form, coverImage: ''})}>
                      <i className="fas fa-times"></i> Rimuovi
                    </button>
                  )}
                </div>
                {form.coverImage && (
                  <img src={form.coverImage} alt="" className="mt-2 w-100 rounded border" style={{ maxHeight: 150, objectFit: 'cover' }} />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Normal list view (hidden when editing) */}
      {!editing && (
        <div className="row g-4">
          <div className="col-lg-8">
            <div className="card border-0 shadow-sm">
          <div className="card-header bg-transparent border-0 pt-4 px-4 d-flex justify-content-between align-items-center">
            <h4 className="mb-0 fw-bold"><i className="fas fa-newspaper text-primary me-2"></i>Articoli Blog</h4>
            <div className="d-flex gap-2">
              <button className="btn btn-sm btn-primary rounded-pill" onClick={handleNew}>
                <i className="fas fa-plus me-1"></i> Nuovo Articolo
              </button>
              <button className="btn btn-sm btn-outline-primary rounded-circle" onClick={fetchPosts}><i className="fas fa-sync"></i></button>
            </div>
          </div>
          <div className="card-body px-4">
            {posts.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="fas fa-newspaper fa-3x mb-3 opacity-25"></i>
                <p>Nessun articolo trovato</p>
              </div>
            ) : (
              <div className="list-group list-group-flush">
                {posts.map(post => (
                  <div key={post.id || post.slug} className="list-group-item d-flex justify-content-between align-items-center py-3 px-0 border-0 border-bottom">
                    <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
                      {post.coverImage && (
                        <img src={post.coverImage} alt="" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 8 }} />
                      )}
                      <div style={{ minWidth: 0 }}>
                        <div className="fw-semibold text-truncate">{post.title}</div>
                        <div className="small text-muted">
                          <span className={`badge ${post.status === 'published' ? 'bg-success' : 'bg-warning'} me-2`}>{post.status}</span>
                          {post.slug}
                          {post.publishedAt && <span className="ms-2">· {new Date(post.publishedAt).toLocaleDateString('it-IT')}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="d-flex gap-2 flex-shrink-0">
                      <button className="btn btn-sm btn-outline-primary rounded-pill" onClick={() => handleEdit(post)}>
                        <i className="fas fa-edit"></i>
                      </button>
                      <button className="btn btn-sm btn-outline-danger rounded-pill" onClick={() => handleDelete(post.slug)}>
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
      )}
    </>
  );
}